import React, { useState } from 'react';
import { messagesAPI } from '../api';
import { Send, CheckCircle2, XCircle, Loader2, Phone, MessageSquare, Plus, Trash2 } from 'lucide-react';

export default function ManualSend({ sessionId }) {
  const [contacts, setContacts] = useState([{ phone: '', name: '' }]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const addContact = () => setContacts([...contacts, { phone: '', name: '' }]);

  const removeContact = (idx) => {
    if (contacts.length === 1) return;
    setContacts(contacts.filter((_, i) => i !== idx));
  };

  const updateContact = (idx, field, value) => {
    const updated = [...contacts];
    updated[idx][field] = value;
    setContacts(updated);
  };

  const handleSend = async () => {
    const valid = contacts.filter(c => c.phone.trim());
    if (valid.length === 0) { setError('Please enter at least one phone number'); return; }
    if (!message.trim()) { setError('Please enter a message'); return; }
    setSending(true);
    setError('');
    setResults([]);

    const resultList = [];
    for (const contact of valid) {
      try {
        await messagesAPI.sendManual({ sessionId, phone: contact.phone.trim(), message });
        resultList.push({ name: contact.name || contact.phone, phone: contact.phone, status: 'sent' });
      } catch (err) {
        resultList.push({
          name: contact.name || contact.phone,
          phone: contact.phone,
          status: 'failed',
          reason: err.response?.data?.error || err.message,
        });
      }
      await new Promise(r => setTimeout(r, 600));
    }
    setResults(resultList);
    setSending(false);
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Phone size={18} className="text-blue-600" />
          Recipients
        </h3>

        <div className="space-y-3">
          {contacts.map((contact, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="text"
                value={contact.name}
                onChange={(e) => updateContact(idx, 'name', e.target.value)}
                placeholder="Name (optional)"
                className="w-36 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={contact.phone}
                onChange={(e) => updateContact(idx, 'phone', e.target.value)}
                placeholder="Phone (e.g. 919876543210)"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-500"
              />
              <button
                onClick={() => removeContact(idx)}
                disabled={contacts.length === 1}
                className="p-2 text-red-400 hover:text-red-600 disabled:opacity-30 transition-colors"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>

        <button onClick={addContact} className="mt-3 btn-3d-blue text-sm py-2 px-4 flex items-center gap-2">
          <Plus size={14} />
          Add Recipient
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <MessageSquare size={18} className="text-blue-600" />
          Message
        </h3>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={8}
          placeholder="Type your message here..."
          className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-y focus:outline-none focus:border-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400 text-right">{message.length} characters</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <XCircle size={18} className="text-red-500 flex-shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {results.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b font-semibold text-gray-700 text-sm">Send Results</div>
          <div className="divide-y divide-gray-100">
            {results.map((r, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm text-gray-800">{r.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{r.phone}</p>
                </div>
                {r.status === 'sent' ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                    <CheckCircle2 size={14} />Sent
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                    <XCircle size={14} />Failed
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={handleSend}
        disabled={sending}
        className={`w-full py-4 rounded-xl font-bold text-white text-lg flex items-center justify-center gap-3 transition-all transform
          ${sending
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-orange-500 hover:bg-orange-600 shadow-[0_6px_0_#c2410c] active:shadow-[0_2px_0_#c2410c] active:translate-y-1'
          }`}
      >
        {sending ? <><Loader2 size={22} className="animate-spin" />Sending...</> : <><Send size={22} />Send Manual Message</>}
      </button>
    </div>
  );
}
